import { queryOptions, useQuery } from '@tanstack/react-query';
import type { DefaultError, UseQueryOptions } from '@tanstack/react-query';

import { toFolder } from '../../api/assetLibraryMapping';
import type { CompanyScopedAssetLibraryClient } from '@ael/core-api';
import type { Folder } from '../../types';
import { mediaLibraryKeys, type FolderTreeKey } from './queryKeys';
import { EMPTY_FOLDER_TREE, type FolderTree, buildFolderTree } from './folderTree';

/**
 * Query 1 of 3: the folder tree.
 *
 * The server sends folders flat, each pointing at its parent, and `select` links them up. Doing
 * it here rather than in the query function keeps the linking out of every cache write: React
 * Query memoises `select` against the raw data, so the tree is rebuilt when the folders change
 * and not once per render.
 */
export const folderTreeOptions = (
  api: CompanyScopedAssetLibraryClient
): UseQueryOptions<Folder[], DefaultError, FolderTree, FolderTreeKey> =>
  queryOptions({
    queryKey: mediaLibraryKeys(api.companyId).folderTree(),
    queryFn: async ({ signal }) => {
      const tree = await api.getAssetDirectoryTree(signal);
      return tree.folders.map(toFolder);
    },
    select: buildFolderTree,
    // Every Folder change the library makes invalidates the tree, so it goes stale only through
    // someone else's change. Long, so the many places that read it -- the preview, search rows,
    // dialogs -- are served from the cache; `MediaLibrary` refetches it on each mount instead.
    staleTime: 5 * 60_000,
    // As the listing does: an unreachable tree is not an empty one, and drawing it empty would
    // invite someone to rebuild folders that already exist.
    throwOnError: true,
  });

/**
 * The folder tree, linked up and ready to walk, and whether it has arrived.
 *
 * Both off one observer: a second `useQuery` on this key would re-run `select` -- rebuilding the
 * whole tree -- to answer a boolean, and would read its status from its own placeholder state
 * rather than from this one.
 *
 * Loading and empty have to be told apart here because they are indistinguishable downstream: the
 * tree falls back to a shared empty one either way, so a tenant with no folders and one whose
 * folders are still on their way would draw the same sidebar.
 *
 * React Query hands back the same `data` reference until the cache changes, which is what keeps
 * `useMemo`s over the tree from recomputing on unrelated renders.
 */
export function useFolderTreeQuery(api: CompanyScopedAssetLibraryClient): { tree: FolderTree; isLoading: boolean } {
  const { data, isPending } = useQuery(folderTreeOptions(api));

  return { tree: data ?? EMPTY_FOLDER_TREE, isLoading: isPending };
}
